import { useMemo } from 'react';
import { components } from '@lifeserver/models/lifev2';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { ChangeLogEntityType } from '@lifeserver/shared';

const DEFAULT_BASE_PATH = '/api';
const CHANGELOG_URL = '/changelog/v2';

export type ChangelogEntryDto = components['schemas']['ChangelogEntryDto'];
export type CreateChangelogDTO = components['schemas']['CreateChangelogDTO'];

export class ChangeLogApi {
    getAll = async ({ basePath = DEFAULT_BASE_PATH } = {}): Promise<
        ChangelogEntryDto[]
    > => {
        const { data } = await axios.get<ChangelogEntryDto[]>(
            `${basePath}${CHANGELOG_URL}`,
        );

        return data;
    };

    createChangeLogEntry = async ({
        createChangeLogEntryDto,
        basePath = DEFAULT_BASE_PATH,
    }: {
        createChangeLogEntryDto: CreateChangelogDTO;
        basePath?: string;
    }): Promise<CreateChangelogDTO> => {
        const response = await axios.post<CreateChangelogDTO>(
            `${basePath}${CHANGELOG_URL}`,
            createChangeLogEntryDto,
        );

        return response.data;
    };
    getByEntity = async ({
        entity,
        entityId,
        basePath = DEFAULT_BASE_PATH,
    }: {
        entity: string;
        entityId?: string;
        basePath?: string;
    }): Promise<ChangelogEntryDto[]> => {
        const params = new URLSearchParams({ entity });
        if (entityId) params.append('entityId', entityId);
        const { data } = await axios.get<ChangelogEntryDto[]>(
            `${basePath}${CHANGELOG_URL}/by-entity?${params.toString()}`,
        );
        return data;
    };
}
export const useChangeLogApi = () => {
    return useMemo(() => new ChangeLogApi(), []);
};

export const useChangeLogs = () => {
    const changeLogApi = useChangeLogApi();

    return useQuery({
        queryKey: [CHANGELOG_URL],
        queryFn: () => changeLogApi.getAll(),
    });
};
export const useChangeLogsByEntity = (
    entity?: ChangeLogEntityType,
    entityId?: string,
) => {
    const changeLogApi = useChangeLogApi();

    return useQuery({
        queryKey: [CHANGELOG_URL, entity, entityId],
        queryFn: entity
            ? () => changeLogApi.getByEntity({ entity, entityId })
            : undefined,
        enabled: !!entity,
    });
};

export const useSaveChangeLog = () => {
    const changeLogApi = useChangeLogApi();
    const client = useQueryClient();
    return useMutation({
        mutationFn: changeLogApi.createChangeLogEntry,
        onSettled: () => {
            client.invalidateQueries({ queryKey: [CHANGELOG_URL] });
        },
    });
};
export function createChangelogDetailsCreated<
    T extends Record<string, unknown>,
>(obj: T): { action: 'created'; created: T } {
    return { action: 'created', created: obj };
}

export function createChangelogDetailsUpdated<
    T extends Record<string, unknown>,
>(oldObj: T, newObj: T): { action: 'updated'; old: T; updated: T } {
    return { action: 'updated', old: oldObj, updated: newObj };
}

export function createChangelogDetailsDeleted<
    T extends Record<string, unknown>,
>(obj: T): { action: 'deleted'; deleted: T } {
    return { action: 'deleted', deleted: obj };
}
